// Creating a tenant user, and the one moment its generated password is
// visible. Used by the onboarding stepper and the tenant detail page — one
// form, two doors, so the password handling cannot drift between them.
import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/gb/field";
import { useCreateUser } from "./tenants-api";

/**
 * The reveal. The password exists only in this component's state: it came off
 * the wire once, and closing or resetting the form is the last anyone sees of
 * it — the server kept only the hash.
 */
function PasswordReveal({
  email,
  password,
  onDone,
}: {
  email: string;
  password: string;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-lg border-[1.5px] border-warn bg-warn-bg p-4">
      <div className="text-[0.85rem] font-semibold text-ink">{email} is ready to sign in.</div>
      <p className="mt-1 text-[0.8rem] leading-[1.5] text-muted">
        This password is shown <strong>once</strong> — it cannot be retrieved again. Pass it on
        securely and have them change it.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <code className="flex-1 rounded-md border border-border bg-white px-3 py-2 font-mono text-[0.85rem] tracking-wide text-ink select-all">
          {password}
        </code>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => {
            void navigator.clipboard.writeText(password).then(() => setCopied(true));
          }}
        >
          {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <div className="mt-3">
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          Add another user
        </Button>
      </div>
    </div>
  );
}

export function UserCreateForm({ tenantId }: { tenantId: string }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const create = useCreateUser(tenantId);

  if (create.data) {
    return (
      <PasswordReveal
        email={create.data.user.email}
        password={create.data.password}
        onDone={() => {
          setEmail("");
          setName("");
          create.reset();
        }}
      />
    );
  }

  const submit = (): void => {
    if (!email.trim() || create.isPending) return;
    create.mutate({ email: email.trim(), name: name.trim() || undefined });
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Field label="Email" error={create.isError ? String(create.error.message) : undefined}>
        {(props) => (
          <Input
            {...props}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="site.manager@customer.com"
            autoComplete="off"
          />
        )}
      </Field>
      <Field label="Name" optional>
        {(props) => (
          <Input
            {...props}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jana Nováková"
            autoComplete="off"
          />
        )}
      </Field>
      <Button type="submit" size="sm" disabled={!email.trim() || create.isPending}>
        {create.isPending ? "Creating…" : "Create user"}
      </Button>
    </form>
  );
}
